import { useCallback, useMemo } from "react";
import { resolveConceptQueryTargetTypes } from "../services/autoTaggingService";
import { useAutoTaggingPlanTypes } from "./useAutoTaggingPlanTypes";
import { useConfigurableConcepts } from "./useConfigurableConcepts";

export function useAssessmentPlanFilterOptions(
  targetType?: string | null,
  subType?: string | null,
) {
  const {
    planTypes,
    objectTypeOptions,
    defaultObjectType,
    getSubTypesForObjectType,
    loading: planTypesLoading,
  } = useAutoTaggingPlanTypes();

  const fallbackTypeCodes = useMemo(
    () => objectTypeOptions.map((item) => item.code).filter(Boolean),
    [objectTypeOptions],
  );

  // Empty Object Type (All) → load concepts for every available type.
  // Sub-Type only → map back to the parent `target_type`(s).
  const conceptTargetTypes = useMemo(
    () =>
      resolveConceptQueryTargetTypes({
        objectType: targetType,
        objectSubType: subType,
        planTypes,
        fallbackTypeCodes,
      }),
    [fallbackTypeCodes, planTypes, subType, targetType],
  );

  const { conceptOptions, loading: conceptsLoading } =
    useConfigurableConcepts(conceptTargetTypes, subType);

  const getObjectSubTypeOptions = useCallback(
    (objectType: string) => {
      if (!objectType) {
        const seen = new Set<string>();
        const options: Array<{ code: string; name: string }> = [];
        for (const planType of planTypes) {
          for (const item of planType.objectSubTypes) {
            if (seen.has(item.code)) continue;
            seen.add(item.code);
            options.push(item);
          }
        }
        return options;
      }
      return getSubTypesForObjectType(objectType);
    },
    [getSubTypesForObjectType, planTypes],
  );

  return {
    planTypes,
    objectTypeOptions,
    defaultObjectType,
    conceptOptions,
    getObjectSubTypeOptions,
    planTypesLoading,
    conceptsLoading,
  };
}
